import { Link, useNavigate, useParams } from "react-router-dom";
import { Shuffle } from "lucide-react";
import { weightedPick } from "../engine/random";
import { BUDGETS, RECIPIENTS, VIBES } from "../data/presents";
import { GIFT_PRESETS, giftPresetBySlug } from "../data/giftPresets";
import { rankPresents } from "../engine/presents";
import { useGiftCatalog } from "../lib/giftCatalog";
import { faqJsonLd, SITE_URL, useSeo } from "../lib/seo";
import { PageShell } from "../components/PageShell";
import { PresentPanel } from "../components/home/PresentPanel";
import NotFound from "./NotFound";

const labelOf = (list: { id: string; label: string }[], id: string) => list.find((x) => x.id === id)?.label || id;

function GiftIndex() {
  const navigate = useNavigate();
  useSeo({
    title: "Gift Ideas by Person & Budget – Present Finder | DITHER",
    description: "Ready-made gift idea lists: gifts for Dad under £50, funny coworker gifts, foodie friends, partners, kids and more. Instant curated suggestions.",
    path: "/gifts",
  });
  return (
    <PageShell title="Gift ideas" subtitle="Pick a list — DITHER pre-fills the Present Finder for you." testId="gifts-index-page">
      <button onClick={() => navigate(`/gifts/${weightedPick(GIFT_PRESETS).slug}`)} data-testid="gift-preset-dither-decide"
        className="ua-btn mb-3 flex w-full items-center justify-center gap-2 py-3 text-lg"><Shuffle className="h-5 w-5" />Dither, pick a list for me</button>
      <div className="grid gap-2 sm:grid-cols-2" data-testid="gift-preset-list">
        {GIFT_PRESETS.map((g) => (
          <Link key={g.slug} to={`/gifts/${g.slug}`} className="ua-tile block rounded-[18px] border-[3px] border-[#FFC0D9] bg-white p-4" data-testid={`gift-preset-${g.slug}`}>
            <span className="font-comic text-lg uppercase text-[#FF2A85]">{g.heading}</span>
            <span className="block text-sm font-semibold text-[#6b3a12]">{g.blurb}</span>
          </Link>
        ))}
      </div>
    </PageShell>
  );
}

function GiftPresetPage({ slug }: { slug: string }) {
  const g = giftPresetBySlug(slug);
  const { gifts } = useGiftCatalog();
  const ideas = g && gifts ? rankPresents({ recipient: g.recipient, budget: g.budget, vibes: [g.vibe] }, 6, gifts) : [];
  const faqs = g ? [
    { q: `What's a good ${g.heading.toLowerCase()}?`, a: `Top picks include ${ideas.slice(0, 3).map((i) => i.name).join(", ")}. Tap Find another for more ideas.` },
    { q: "How does the DITHER Present Finder choose?", a: "It scores a curated list of gift ideas by who it's for, your budget and their vibe, then shows the best matches first." },
  ] : [];
  useSeo({
    title: g?.title || "Gift ideas | DITHER",
    description: g ? `${g.blurb} Ideas include ${ideas.slice(0, 3).map((i) => i.name.toLowerCase()).join(", ")} and more.` : "",
    path: `/gifts/${slug}`,
    jsonLd: g ? [faqJsonLd(faqs), {
      "@context": "https://schema.org", "@type": "ItemList", name: g.heading,
      itemListElement: ideas.map((i, n) => ({ "@type": "ListItem", position: n + 1, name: i.name, url: `${SITE_URL}/gifts/${slug}` })),
    }] : undefined,
  });
  if (!g) return <NotFound />;
  return (
    <PageShell title={g.heading} subtitle={g.blurb} back="/presents" testId="gift-preset-page">
      <p className="mb-3 text-xs font-extrabold uppercase tracking-widest text-[#8a5a2b]" data-testid="gift-preset-filters">
        {labelOf(RECIPIENTS, g.recipient)} · {labelOf(BUDGETS, g.budget)} · {labelOf(VIBES, g.vibe)}
      </p>
      <PresentPanel big initial={{ recipient: g.recipient, budget: g.budget, vibe: g.vibe }} autoRun />
    </PageShell>
  );
}

export default function GiftsPage() {
  const { slug } = useParams();
  return slug ? <GiftPresetPage key={slug} slug={slug} /> : <GiftIndex />;
}
